import type { ReactNode } from 'react';
import { Badge } from '@indipia/ui-core';

const DOW = ['일', '월', '화', '수', '목', '금', '토'];

/** 'YYYY-MM-DD' → '6/22(월)' (요일 계산). 파싱 실패 시 원문 반환. */
function formatRequestDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const [, y, mo, d] = m;
  const dow = DOW[new Date(Number(y), Number(mo) - 1, Number(d)).getDay()];
  return `${Number(mo)}/${Number(d)}(${dow})`;
}

/** 좌측 액센트 바가 붙는 섹션 라벨 (--gt-primary) */
export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <span className="flex items-center gap-1.5 text-caption font-bold" style={{ color: 'var(--shell-hint)', letterSpacing: '.3px' }}>
      <span style={{ width: 3, height: 10, borderRadius: 'var(--radius-xs)', background: 'var(--gt-primary)' }} />
      {children}
    </span>
  );
}

/** WorkOrderNoticeView가 렌더하는 텍스트 필드 — WorkOrderNotice(서버 응답)의 부분집합이라 그대로 넘겨도 된다. */
export interface WorkOrderNoticeViewData {
  senderCompanyName: string;
  senderRepName: string;
  requestedByLabel: string;
  recipientName: string;
  /** 'YYYY-MM-DD' */
  requestDate: string;
  siteName: string;
  customerName: string;
  orderContent: string;
}

/**
 * 작업요청 알림톡 렌더 결과 공통 뷰 — 반장 GUEST 열람화면(WorkOrderNoticePage)과
 * 등록화면 미리보기 모달(REQ-1-11)이 함께 쓴다(§1339 CustomerReportView와 같은 공유·미리보기 공통 렌더 관례).
 * 사진 로딩 방식(로컬 파일 objectURL vs 서버 인증 썸네일)이 컨텍스트마다 달라
 * 사진 그리드 렌더는 renderThumb 렌더프롭으로 위임한다(PhotoLightbox.renderImage와 동일 관례).
 * 상위 트리에 data-group="02"(D-08 §6.4)가 있어야 --gt-* 토큰이 해석된다.
 */
export function WorkOrderNoticeView<T>({
  data,
  photos,
  renderThumb,
  onPhotoClick,
  afterPhotos,
  hideEmptyOrderContent = false,
}: {
  data: WorkOrderNoticeViewData;
  photos: T[];
  renderThumb: (photo: T, index: number) => ReactNode;
  /** 미지정 시 사진은 비클릭 정적 썸네일로만 표시(미리보기 모달 기본값) */
  onPhotoClick?: (index: number) => void;
  /** 참고 사진 섹션 다음에 붙는 영역(반장 화면의 고객 요청 카드). 미지정 시 아무것도 그리지 않음 */
  afterPhotos?: ReactNode;
  /** true 면 요청내용이 공백일 때 '작업 요청내용' 섹션을 숨김(반장 화면 전용, 기본 false = 기존 동작) */
  hideEmptyOrderContent?: boolean;
}) {
  return (
    <>
      {/* ─ 헤더: 업체 브랜딩 + 요청 타이틀 ─ */}
      <header style={{ background: 'var(--shell-surface)', borderBottom: 'var(--border-width-hairline) solid var(--shell-border)' }}>
        <div style={{ padding: '14px 16px 12px', borderBottom: 'var(--border-width-hairline) solid var(--shell-border)' }}>
          <div className="text-body-md font-bold" style={{ color: 'var(--shell-on-surface)', letterSpacing: '-.2px' }}>
            {data.senderCompanyName}
          </div>
          <div className="mt-0.5 text-caption" style={{ color: 'var(--shell-hint)' }}>대표 {data.senderRepName}</div>
        </div>
        <div style={{ padding: '14px 16px 0' }}>
          <h1 className="text-[15px] font-bold" style={{ color: 'var(--shell-on-surface)', letterSpacing: '-.2px', paddingBottom: 16 }}>
            <span style={{ color: 'var(--gt-primary)' }}>{formatRequestDate(data.requestDate)}</span>
            &nbsp; 작업요청 안내
          </h1>
        </div>
      </header>

      {/* ─ 정보 그리드 ─ */}
      <section
        className="mt-2"
        style={{ background: 'var(--shell-surface)', padding: '12px 16px', borderTop: 'var(--border-width-hairline) solid var(--shell-border)', borderBottom: 'var(--border-width-hairline) solid var(--shell-border)' }}
      >
        <dl className="grid text-body-sm" style={{ gridTemplateColumns: '64px 1fr', rowGap: 8 }}>
          {([
            ['현장명', data.siteName],
            ['고객명', data.customerName],
            ['요청자', data.requestedByLabel],
            ['수신자', data.recipientName],
          ] as const).map(([label, value]) => (
            <div key={label} className="contents">
              <dt style={{ color: 'var(--shell-hint)', fontWeight: 600 }}>{label}</dt>
              <dd style={{ color: 'var(--shell-on-surface)', fontWeight: 500 }}>{value}</dd>
            </div>
          ))}
        </dl>
      </section>

      {/* ─ 요청 내용 ─ */}
      {!(hideEmptyOrderContent && data.orderContent.trim() === '') && (
      <section
        className="mt-2"
        style={{ background: 'var(--shell-surface)', padding: '12px 16px 14px', borderTop: 'var(--border-width-hairline) solid var(--shell-border)', borderBottom: 'var(--border-width-hairline) solid var(--shell-border)' }}
      >
        <SectionLabel>작업 요청내용</SectionLabel>
        <p
          className="mt-2 whitespace-pre-line text-[13px]"
          style={{ color: 'var(--shell-on-surface)', lineHeight: 1.7, background: 'var(--gt-subtle)', border: 'var(--border-width-hairline) solid var(--gt-subtle)', borderRadius: 'var(--radius-md)', padding: '12px 14px' }}
        >
          {data.orderContent}
        </p>
      </section>
      )}

      {/* ─ 참고 사진 ─ */}
      {photos.length > 0 && (
        <section
          className="mt-2"
          style={{ background: 'var(--shell-surface)', padding: '12px 16px 16px', borderTop: 'var(--border-width-hairline) solid var(--shell-border)', borderBottom: 'var(--border-width-hairline) solid var(--shell-border)' }}
        >
          <div className="mb-2.5 flex items-center justify-between">
            <SectionLabel>참고 사진</SectionLabel>
            <Badge className="!rounded-full">{photos.length}장</Badge>
          </div>
          <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            {photos.map((p, i) => (
              <button
                type="button"
                key={i}
                onClick={onPhotoClick ? () => onPhotoClick(i) : undefined}
                disabled={!onPhotoClick}
                className="relative overflow-hidden"
                style={{ aspectRatio: '4 / 3', borderRadius: 'var(--radius-md)', background: 'var(--shell-skeleton)', cursor: onPhotoClick ? 'pointer' : 'default', padding: 0, border: 'none' }}
              >
                {renderThumb(p, i)}
              </button>
            ))}
          </div>
        </section>
      )}

      {afterPhotos}
    </>
  );
}
